interface InlineDiffProps {
  originalText: string
  suggestedText: string
}

type DiffToken = { type: 'equal' | 'delete' | 'insert'; word: string }

/**
 * Compute the Longest Common Subsequence table for two word arrays.
 * Returns a 2D array where lcs[i][j] = length of LCS of a[0..i-1] and b[0..j-1].
 */
function buildLcsTable(a: string[], b: string[]): number[][] {
  const m = a.length
  const n = b.length
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1])
      }
    }
  }
  return dp
}

/**
 * Backtrack through the LCS table to produce a list of diff tokens.
 */
function computeDiff(original: string[], suggested: string[]): DiffToken[] {
  const dp = buildLcsTable(original, suggested)
  const tokens: DiffToken[] = []
  let i = original.length
  let j = suggested.length

  // Backtrack from bottom-right, collecting tokens in reverse
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && original[i - 1] === suggested[j - 1]) {
      tokens.push({ type: 'equal', word: original[i - 1] })
      i--
      j--
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      tokens.push({ type: 'insert', word: suggested[j - 1] })
      j--
    } else {
      tokens.push({ type: 'delete', word: original[i - 1] })
      i--
    }
  }

  return tokens.reverse()
}

export function InlineDiff({ originalText, suggestedText }: InlineDiffProps) {
  const originalWords = originalText.split(/\s+/).filter(Boolean)
  const suggestedWords = suggestedText.split(/\s+/).filter(Boolean)
  const tokens = computeDiff(originalWords, suggestedWords)

  return (
    <div className="bg-muted rounded-lg p-2 px-2.5 text-[11px] leading-relaxed text-foreground">
      {tokens.map((token, i) => {
        const space = i > 0 ? ' ' : ''
        switch (token.type) {
          case 'equal':
            return <span key={i}>{space}{token.word}</span>
          case 'delete':
            return (
              <span key={i}>
                {space}
                <span className="text-[var(--status-error-text)] line-through">{token.word}</span>
              </span>
            )
          case 'insert':
            return (
              <span key={i}>
                {space}
                <span className="text-[var(--status-verified-text)] bg-[var(--status-verified-bg)] rounded-sm px-0.5">
                  {token.word}
                </span>
              </span>
            )
        }
      })}
    </div>
  )
}
